/**
 * Property names that React, devtools, JSON.stringify, Promise/thenable
 * detection, jest matchers, Immutable.js, and similar runtimes probe on every
 * Proxy they touch. Any access-guarded Proxy that warns on unknown property
 * reads must fall through silently for these — otherwise the warning fires on
 * every render or every JSON serialization.
 *
 * Use `isRuntimeProbeProp(prop)` from any Proxy `get` trap that warns on
 * unknown access:
 *
 *   if (isRuntimeProbeProp(prop)) return undefined; // silent fallthrough
 *   console.warn('[my-proxy] unknown property', prop);
 *
 * Each ecosystem stamps its own marker; there is no shared registry, so this
 * list is curated from React/Vue/MobX/Immutable/jest internals seen in the
 * wild. Add to it (and add a test) when a new probe surfaces.
 */

export const RUNTIME_PROBE_PROPS: ReadonlySet<string> = new Set([
  // React element / fiber markers
  '$$typeof',
  '_owner',
  '_store',
  'ref',
  'key',
  // Suspense / await thenable check
  'then',
  'catch',
  'finally',
  // DOM-like duck-type checks
  'nodeType',
  'tagName',
  'nodeName',
  // JSON / coercion
  'toJSON',
  'toString',
  'valueOf',
  // Object.prototype probes
  'constructor',
  'prototype',
  'hasOwnProperty',
  'isPrototypeOf',
  'propertyIsEnumerable',
  // jest asymmetric matcher probe
  'asymmetricMatch',
  // Immutable.js iterable / record / list / map / set / ordered / keyed sigils
  '@@__IMMUTABLE_ITERABLE__@@',
  '@@__IMMUTABLE_RECORD__@@',
  '@@__IMMUTABLE_LIST__@@',
  '@@__IMMUTABLE_MAP__@@',
  '@@__IMMUTABLE_SET__@@',
  '@@__IMMUTABLE_ORDERED__@@',
  '@@__IMMUTABLE_KEYED__@@',
]);

/**
 * Returns true when `prop` is a runtime/devtools/library probe that any
 * access-guarded Proxy should silently ignore (return undefined without
 * logging).
 *
 * Covers the curated `RUNTIME_PROBE_PROPS` set plus two pattern classes:
 *   - `@@…` — Immutable.js / observer protocol sigils
 *   - `__react…` — React fiber / internal-instance fields (`__reactFiber$xyz`,
 *     `__reactInternalInstance`, etc.)
 */
export const isRuntimeProbeProp = (prop: string): boolean =>
  RUNTIME_PROBE_PROPS.has(prop) || prop.startsWith('@@') || prop.startsWith('__react');
